import Loading from '@/components/Loading/Loading';
import { GraphCanvas } from '@/components/UI/GraphCanvas/GraphCanvas';
import { createGraphCanvasContext } from '@/components/UI/GraphCanvas/GraphCanvasContext';
import type { GraphCanvasEngine } from '@/components/UI/GraphCanvas/GraphCanvasEngine';
import ButtonNode from '@/components/UI/GraphCanvas/nodes/ButtonNode';
import ReadOnlyNode from '@/components/UI/GraphCanvas/nodes/ReadOnlyNode';
import Icon from '@/components/UI/Icon/Icon';
import { _isNil } from '@/littledash';
import type { AgentSessionApiId, AgentTreeV1 } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { Panel, type NodeTypes } from '@xyflow/react';
import { type FC, useMemo } from 'react';
import { RiCheckboxMultipleFill } from 'react-icons/ri';
import type { AgentActivityEngine } from './Activity/AgentActivityEngine';
import ActivityPanelContent from './Activity/ActivityPanelContent';
import AgentSessionPanel from './AgentSessionPanel';
import styles from './AgentSessionView.module.scss';
import {
  buildAgentTreeFitViewOptions,
  type AgentSessionMeta,
  type AgentTreeNodeData,
  type ViewReportHandler,
} from './AgentTree.utils';
import { useActivityRows } from './Activity/useActivityRows';
import { type HistoryState, useAgentSessionStream } from './useAgentSessionStream';

/** Node component registry, keyed on node variant → generic GraphCanvas node shells. Module scope. */
const nodeTypes: NodeTypes = {
  read_only: ReadOnlyNode,
  report: ButtonNode,
};

/** fitView padding/zoom that keeps nodes clear of the overlay panels (see AgentTree.utils). Module scope. */
const fitViewOptions = buildAgentTreeFitViewOptions();

/** The agent tree's fully-typed GraphCanvas provider + hooks (one instantiation for this feature). */
const { GraphCanvasProvider, useGraphCanvasGraph } = createGraphCanvasContext<AgentTreeNodeData, AgentSessionMeta>();

/** Reads the engine via hooks and renders the read-only canvas + panels. Must sit under GraphCanvasProvider. */
const AgentSessionTreeCanvas: FC<{ activityEngine: AgentActivityEngine; historyState: HistoryState }> = ({
  activityEngine,
  historyState,
}) => {
  const { nodes, edges } = useGraphCanvasGraph();
  return (
    <GraphCanvas<AgentTreeNodeData> nodes={nodes} edges={edges} nodeTypes={nodeTypes} fitViewOptions={fitViewOptions}>
      <Panel position="top-left">
        <AgentSessionPanel
          id="session-configuration"
          title="Configuration"
          icon={<Icon icon="cog" className={styles.panelIcon} />}
        />
      </Panel>
      <Panel position="top-right">
        <AgentSessionPanel
          id="session-activity"
          title="Activity"
          icon={<RiCheckboxMultipleFill className={styles.panelIcon} />}
        >
          <ActivityPanel activityEngine={activityEngine} historyState={historyState} />
        </AgentSessionPanel>
      </Panel>
    </GraphCanvas>
  );
};

/** Renders the derived Activity rows (live or history — same store), or loading/error state. */
const ActivityPanel: FC<{ activityEngine: AgentActivityEngine; historyState: HistoryState }> = ({
  activityEngine,
  historyState,
}) => {
  const rows = useActivityRows(activityEngine);
  if (historyState === 'loading') {
    return (
      <p data-test-element="activity-loading" className={styles.status}>
        Loading activity…
      </p>
    );
  }
  if (historyState === 'error') {
    return (
      <p data-test-element="activity-error" className={styles.status}>
        Couldn't load activity history.
      </p>
    );
  }
  return <ActivityPanelContent rows={rows} />;
};

interface AgentSessionTreeViewProps {
  engine: GraphCanvasEngine<AgentTreeNodeData, AgentSessionMeta>;
  activityEngine: AgentActivityEngine;
  historyState: HistoryState;
}

/**
 * Pure presentational shell: the React Flow canvas + Configuration/Activity panels. No stream
 * subscriptions and no reconnect UI — the ready child (below) owns the live wiring, and the transport
 * self-heals dropped connections via managed backoff, so there is no manual "retry" affordance.
 */
const AgentSessionTreeView: FC<AgentSessionTreeViewProps> = ({ engine, activityEngine, historyState }) => (
  <div className={styles.container} data-test-component="AgentSessionTreeView">
    <GraphCanvasProvider store={engine}>
      <AgentSessionTreeCanvas activityEngine={activityEngine} historyState={historyState} />
    </GraphCanvasProvider>
  </div>
);

interface AgentSessionOrchestratorProps {
  sessionApiId: AgentSessionApiId;
  initialTree: AgentTreeV1;
  onViewReport: ViewReportHandler;
}

/**
 * Ready child: mounted once (never conditionally unmounted) so the live wiring survives the
 * `running → terminal` transition. All the imperative machinery — engines, SSE transport, reconcile,
 * history replay — lives in {@link useAgentSessionStream}; this component just renders what it returns.
 */
const AgentSessionOrchestrator: FC<AgentSessionOrchestratorProps> = ({ sessionApiId, initialTree, onViewReport }) => {
  const { engine, activityEngine, historyState } = useAgentSessionStream(sessionApiId, initialTree, onViewReport);
  return <AgentSessionTreeView engine={engine} activityEngine={activityEngine} historyState={historyState} />;
};

interface AgentSessionViewProps {
  sessionApiId: AgentSessionApiId;
  onViewReport: ViewReportHandler;
}

/**
 * Outer loader for an agent session: fetches `GET /tree` once (rendering loading/error/success), then
 * hands the settled snapshot to the always-mounted ready child, which owns the engine, the activity
 * store, the live stream, and all reconciliation. Replaces the snapshot-only `AgentSessionTree`.
 */
const AgentSessionView: FC<AgentSessionViewProps> = ({ sessionApiId, onViewReport }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/tree',
    path: { sessionApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-session-tree-load',
      },
    },
  });

  // The ready child must not remount on `response` identity changes (useApiHook re-runs), so pin the
  // first successful body — after which the child owns all further /tree fetches via reconcile().
  const initialTree = useMemo(() => (response?.type === 'success' ? response.body : null), [response]);

  if (loading) {
    return (
      <div className={styles.container} data-test-component="AgentSessionView">
        <Loading txt="Loading session tree…" />
      </div>
    );
  }
  if (_isNil(initialTree)) {
    return (
      <div className={styles.container} data-test-component="AgentSessionView">
        <p className={styles.status} data-test-element="error">
          Couldn't load the session tree.
        </p>
      </div>
    );
  }
  return <AgentSessionOrchestrator sessionApiId={sessionApiId} initialTree={initialTree} onViewReport={onViewReport} />;
};

export default AgentSessionView;
